import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  IconButton,
  TextField,
  Button,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CommonLoader from "../../common/CommonLoader";
import ViewSampleType from "./ViewSampleType";
import DeleteConfirmation from "../../common/DeleteConfirmation";
import {
  GetSampleTypes,
  DeleteSampleType,
} from "../../../services/SamplesMaintenanceService";
import AddSampleTypeDialog from "./AddSampleTypeDialog"; // Import the dialog component
import { NotificationManager } from "react-notifications";

const SampleTypesTable = () => {
  const columns = ["Sample Type", "Turnaround", "Actions"];
  const columnHeaders = ["samtype", "turnaround"];
  const rowsPerPageOptions = [10, 25, 50, 100, 200];

  const [sampleTypesData, setSampleTypesData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSampleType, setSelectedSampleType] = useState(null);
  const [editSampleType, setEditSampleType] = useState(null);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false);
  const [selectedItemToDelete, setSelectedItemToDelete] = useState(null);
  const [isNewSampleDialogOpen, setIsNewSampleDialogOpen] = useState(false); // State for new sample dialog

  useEffect(() => {
    fetchSampleTypes();
  }, []);

  const fetchSampleTypes = async () => {
    try {
      setLoading(true);
      const response = await GetSampleTypes();
      setSampleTypesData(response.results);
      setFilteredData(response.results);
    } catch (error) {
      NotificationManager.error("Error fetching sample types:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const filteredResults = sampleTypesData.filter((sampleType) =>
      columnHeaders.some((header) => {
        const columnValue = sampleType[header];
        return (
          columnValue !== null &&
          columnValue !== undefined &&
          columnValue.toLowerCase().includes(searchTerm.toLowerCase())
        );
      })
    );
    setFilteredData(filteredResults);
  }, [searchTerm, sampleTypesData]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewClick = (sampleType) => {
    setSelectedSampleType(sampleType);
  };

  const handleBackClick = () => {
    setSelectedSampleType(null);
    setSearchTerm("");
  };

  const handleAddSampleTypeClick = () => {
    setIsNewSampleDialogOpen(true); // Open the new sample dialog
  };

  const handleDeleteClick = (sampleType) => {
    setSelectedItemToDelete(sampleType);
    setIsDeleteConfirmationOpen(true);
  };

  const handleDeleteConfirmationClose = () => {
    setIsDeleteConfirmationOpen(false);
  };

  const handleDeleteConfirmationConfirm = async () => {
    try {
      const response = await DeleteSampleType(selectedItemToDelete.samtype);
      if (response.results === true) {
        setIsDeleteConfirmationOpen(false);
        const updatedSampleTypes = sampleTypesData.filter(
          (item) => item.samtype !== selectedItemToDelete?.samtype
        );
        setSampleTypesData(updatedSampleTypes);
        setFilteredData(updatedSampleTypes);
        NotificationManager.success("Sample type deleted successfully");
      } else {
        NotificationManager.error("Error deleting sample type");
        setIsDeleteConfirmationOpen(false);
      }
    } catch (error) {
      NotificationManager.error("Error deleting sample type");
      setIsDeleteConfirmationOpen(false);
    }
  };

  const handleNewSampleDialogClose = () => {
    setIsNewSampleDialogOpen(false); // Close the new sample dialog
  };

  const handleNewSampleDialogAdd = (sampleType, turnaround) => {
    setIsNewSampleDialogOpen(false);
  };

  const handleEditClick = (sampleType) => {
    setEditSampleType(sampleType);
    setIsNewSampleDialogOpen(true);
  };

  const renderActions = (sampleType) => (
    <div style={{ minWidth: "120px" }}>
      <IconButton
        aria-label="edit"
        style={{ color: "orange" }}
        onClick={() => handleEditClick(sampleType)}
      >
        <EditIcon />
      </IconButton>
      <IconButton
        aria-label="view"
        style={{ color: "green" }}
        onClick={() => handleViewClick(sampleType)}
      >
        <VisibilityIcon />
      </IconButton>
      <IconButton
        aria-label="delete"
        style={{ color: "red" }}
        onClick={() => handleDeleteClick(sampleType)}
      >
        <DeleteIcon />
      </IconButton>
    </div>
  );

  return (
    <>
    <span className="title">Test Sample Types </span>
    <hr />
      {selectedSampleType ? (
        <>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBackClick}
            style={{ marginBottom: "16px", color: "green" }}
          >
            Back
          </Button>
          <ViewSampleType sampleType={selectedSampleType} />
        </>
      ) : (
        <>
          <div
            style={{
              display: "flex",
              marginBottom: "16px",
              alignItems: "center",
            }}
          >
            <TextField
              label="Search"
              variant="outlined"
              value={searchTerm}
              size="small"
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ marginRight: "16px", width: "80%" }}
            />
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddSampleTypeClick}
            >
              New
            </Button>
          </div>
          <Paper style={{ marginRight: "20px" }}>
            <Table sx={{ minWidth: 400 }} size="small">
              <TableHead
                sx={{
                  backgroundColor: (theme) => theme.palette.primary.main,
                  color: "white",
                }}
              >
                <TableRow>
                  {columns.map((column) => (
                    <TableCell key={column}>{column}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredData
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((sampleType, index) => (
                    <TableRow
                      key={sampleType.samtype}
                      sx={{
                        backgroundColor:
                          index % 2 === 0 ? "rgba(0, 0, 0, 0.04)" : "white",
                      }}
                    >
                      {columnHeaders.map((column) => (
                        <TableCell key={column}>{sampleType[column]}</TableCell>
                      ))}
                      <TableCell>{renderActions(sampleType)}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={rowsPerPageOptions}
              component="div"
              count={filteredData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>

          <CommonLoader loading={loading} />

          {/* Render the delete confirmation popup */}
          <DeleteConfirmation
            isOpen={isDeleteConfirmationOpen}
            onClose={handleDeleteConfirmationClose}
            onConfirm={handleDeleteConfirmationConfirm}
            title="Delete Confirmation"
            message={`Are you sure you want to delete the ${selectedItemToDelete?.samtype}?`}
            color="red"
          />

          {/* Render the new sample type dialog */}
          <AddSampleTypeDialog
            open={isNewSampleDialogOpen}
            onClose={handleNewSampleDialogClose}
            onAdd={handleNewSampleDialogAdd}
            editSampleType={editSampleType}
          />
        </>
      )}
    </>
  );
};

export default SampleTypesTable;
